import Cookies from "js-cookie";
import queryString from "query-string";
import cloneDeep from "lodash/cloneDeep";
// import { fromJS } from 'immutable';
import { LOCATION_CHANGE, push } from "react-router-redux";
import { call, cancel, put, take, takeLatest } from "redux-saga/effects";
import { personalUrl, developByCriteriaURL, teamsUrl } from "utils/env";
import request from "utils/request";
import {
  getConfigCatObject,
  checkIfUserHasPosthireAccess,
} from "utils/helperFunctions";
import {
  segmentIdentifyUser,
  segmentIdentifyGroup,
  trackSSOLogin,
  trackFTUserAssignedCTA,
  trackPostHireV2Login,
} from "utils/trackingFunctions";
import jwtDecode from "jwt-decode";
import {
  LOAD_SEARCH_FILTER_OPTIONS,
  LOGIN,
  AUTHORIZE_LINKEDIN_INTEGRATION,
  PARTNERS_LOGIN,
  PARTNERS_FORGOT_PASSWORD,
  RESET_PASSWORD,
  GET_INVOICE,
  GET_COMPANY_INFO,
  GET_ROLES,
  GET_BILLING_INFO,
  SET_NEW_PASSWORD,
  PARTNERS_SET_NEW_PASSWORD,
  GET_INVOICE_PRICING,
  GET_USERS,
  GET_SOURCES,
  CREATE_SOURCE,
  GET_IDENTITY_PROVIDER,
  REFRESH_USER_DATA,
  SSO_LOGIN,
  UPDATE_FIRST_TEAMS_LOGIN,
  DELETE_CANDIDATE,
  GET_DYNAMIC_SCORE_REPORTS_DATA,
  GET_DYNAMIC_SCORE_REPORTS_WORK_STYLES,
  GET_TEST_BATTERIES,
  SEND_SUPPORT_BOT_QUESTION,
  SEND_RESPONSE_FEEDBACK,
  GET_CANDIDATE_ANALYTICS,
  GET_PREV_CANDIDATE_ANALYTICS,
  GET_ASSESSMENT_ANALYTICS,
  GET_PREV_ASSESSMENT_ANALYTICS,
  GET_STRUCTURED_INTERVIEW_ANALYTICS,
  GET_SCORE_RANGE_ANALYTICS,
  GET_BUSINESS_VALUE,
  TRACK_POSTHIRE_LOGIN,
  SET_PASSWORD,
  RESEND_VERIFICATION_EMAIL,
  CHECK_VALID_VERIFY_TOKEN,
} from "./constants";
import {
  getBillingInfoError,
  getBillingInfoSuccess,
  getCompanyInfo,
  getCompanyInfoError,
  getCompanyInfoSuccess,
  getInvoiceError,
  getInvoiceSuccess,
  loadSearchFilterOptionsError,
  loadSearchFilterOptionsSuccess,
  loginError,
  loginSuccess,
  refreshUserDataSuccess,
  showLinkedInAuthorizeModal,
  authorizeLinkedInIntegration,
  linkedInIntegrationError,
  getLoginTokenLinkedIn,
  partnersLoginError,
  partnersLoginSuccess,
  partnersForgotPasswordSuccess,
  partnersForgotPasswordError,
  resetPasswordError,
  getRoles,
  getRolesSuccess,
  getRolesError,
  resetPasswordSuccess,
  setNewPasswordError,
  setNewPasswordSuccess,
  setNewPasswordPartnersError,
  setNewPasswordPartnersSuccess,
  getInvoicePricingSuccess,
  getInvoicePricingError,
  getDynamicScoreReportsDataSuccess,
  getDynamicScoreReportsDataError,
  getUsersSuccess,
  getUsersError,
  getSourcesSuccess,
  getSourcesError,
  createSourceSuccess,
  createSourceError,
  getIdentityProviderSuccess,
  getIdentityProviderError,
  ssoLoginSuccess,
  ssoLoginError,
  updateFirstTeamsLoginSuccess,
  updateFirstTeamsLoginError,
  setupFeatureFlag,
  updateSourcesByLocale,
  deleteCandidateSuccess,
  deleteCandidateError,
  getDynamicScoreReportsWorkStylesSuccess,
  getDynamicScoreReportsWorkStylesError,
  getTestBatteriesSuccess,
  getTestBatteriesError,
  sendSupportBotQuestionSuccess,
  sendSupportBotQuestionError,
  sendResponseFeedbackSuccess,
  sendResponseFeedbackError,
  getCandidateAnalyticsSuccess,
  getCandidateAnalyticsError,
  getPrevCandidateAnalyticsSuccess,
  getPrevCandidateAnalyticsError,
  getAssessmentAnalyticsSuccess,
  getAssessmentAnalyticsError,
  getPrevAssessmentAnalyticsSuccess,
  getPrevAssessmentAnalyticsError,
  getStructuredInterviewAnalyticsSuccess,
  getStructuredInterviewAnalyticsError,
  getScoreRangeAnalyticsSuccess,
  getScoreRangeAnalyticsError,
  getBusinessValueSuccess,
  getBusinessValueError,
  trackPosthireLoginSuccess,
  trackPosthireLoginError,
  refreshUserDataError,
  setPasswordError,
  setPasswordSuccess,
  resendVerificationEmailSuccess,
  resendVerificationEmailError,
  checkValidVerifyTokenSuccess,
  checkValidVerifyTokenError,
} from "./actions";
import {
  getCandidateCounts,
  updateCandidatesAfterDelete,
} from "../Results/actions";
import {
  getVersionName,
  setChurnZeroData,
  handleRememberMe,
  getIdpLogoutUrl,
  signInUserIntoDevelopViaSSO,
  // getSecuredLoginParams,
  // getIdpLoginRedirectUrl
} from "./helpers";
const partnersCenterUrl = `${personalUrl.slice(0, -3)}partnerCenter/`;

/* ---------- SEARCH FILTERS ----------- */

export function* filterOptions() {
  const watch = yield takeLatest(
    LOAD_SEARCH_FILTER_OPTIONS,
    onLoadSearchFilterOptions
  );
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* onLoadSearchFilterOptions() {
  const requestURL = `${personalUrl}candidates/searchOptions`;
  try {
    const loadSearchFilterOptionsResponse = yield call(request, requestURL, {
      method: "get",
    });
    yield put(loadSearchFilterOptionsSuccess(loadSearchFilterOptionsResponse));
  } catch (err) {
    yield put(loadSearchFilterOptionsError(err));
  }
}

/* ----------- Login ----------- */

export function* login() {
  const watch = yield takeLatest(LOGIN, loginSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* loginSaga(data) {
  const requestURL = `${personalUrl}login`;
  try {
    const loginResponse = yield call(request, requestURL, {
      method: "post",
      body: JSON.stringify(data.formData),
    });

    if (loginResponse.jwt && loginResponse.data && !data.liOnboarding) {
      // we need to determine if the logged in user has access to hireselect, teaming, or both, so
      // we can redirect them appropriately.
      const userData = loginResponse.data;
      const hasHireSelectAccess = userData.hireselectAccess;
      const hasPosthireAccess = checkIfUserHasPosthireAccess(userData);
      const hasDevelopByCriteriaAccess = userData.developByCriteriaAccess;
      const hsVersion = getVersionName(userData.hsVersion);

      const configCatObject = yield getConfigCatObject(userData);

      // determine if we need to redirect to the my_summary route of Teams (this is done
      // when the user is logging in to view theirs or someone else's results).  we also
      // need to check to see which userAccountId to view.  if coming from a re-click of
      // the View Insights button from finishing their EPP, we want to use the logged in
      // user id.  if viewing someone else's summary, we will want to use that user's id.
      if (Cookies.get()["rdt-sum"] === "1") {
        const userAccountIdToUse =
          Cookies.get()["rdt-uid"] || userData.userAccountId;
        Cookies.remove("rdt-sum", {
          path: "/",
          domain: ".criteriacorp.com",
        });
        Cookies.remove("rdt-uid", {
          path: "/",
          domain: ".criteriacorp.com",
        });
        Cookies.set("critToken", loginResponse.jwt, {
          path: "/",
          domain: ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          loginResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.analytics.track(
          "talent insights login",
          {
            "referral source type": "url",
            url: `${teamsUrl}/profile/${userAccountIdToUse}`,
          },
          () => {
            window.location.href = `${teamsUrl}/profile/${userAccountIdToUse}`;
          }
        );
        return;
      }

      if (Cookies.get()["rdt-team"] === "1") {
        const teamIdToUse = Cookies.get()["rdt-tid"];
        Cookies.remove("rdt-team", {
          path: "/",
          domain: ".criteriacorp.com",
        });
        Cookies.remove("rdt-tid", {
          path: "/",
          domain: ".criteriacorp.com",
        });
        Cookies.set("critToken", loginResponse.jwt, {
          path: "/",
          domain:
            new URL(teamsUrl).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          loginResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.analytics.track(
          "talent insights login",
          {
            "referral source type": "url",
            url: `${teamsUrl}team/${teamIdToUse}`,
          },
          () => {
            window.location.href = `${teamsUrl}/team/${teamIdToUse}`;
          }
        );
        return;
      }

      if (Cookies.get()["rdt-ti"] === "1") {
        Cookies.set("critToken", loginResponse.jwt, {
          path: "/",
          domain:
            new URL(teamsUrl).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });
        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          loginResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.analytics.track(
          "talent insights login",
          {
            "referral source type": "url",
            url: `${teamsUrl}`,
          },
          () => {
            window.location.href = `${teamsUrl}`;
          }
        );
        return;
      }

      yield put(setupFeatureFlag(configCatObject));

      if (
        !hasDevelopByCriteriaAccess &&
        !hasHireSelectAccess &&
        !hasPosthireAccess
      ) {
        yield put(loginError(6)); // errorCode 6 means that the user does not have access to the prehire or posthire platform
        return;
      }

      if (hasDevelopByCriteriaAccess && !hasHireSelectAccess) {
        Cookies.set("critToken", loginResponse.jwt, {
          path: "/",
          domain:
            new URL(developByCriteriaURL).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          loginResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.location.href = developByCriteriaURL;
        return;
      }

      if (
        hasPosthireAccess &&
        !hasHireSelectAccess &&
        !hasDevelopByCriteriaAccess
      ) {
        Cookies.set("critToken", loginResponse.jwt, {
          path: "/",
          domain:
            new URL(teamsUrl).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          loginResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.analytics.track(
          "talent insights login",
          {
            "referral source type": "url",
            url: teamsUrl,
          },
          () => {
            window.location.href = teamsUrl;
          }
        );
      }

      if (hasHireSelectAccess) {
        const dateToday = new Date();
        const dateExpired = new Date(dateToday);

        dateExpired.setDate(dateExpired.getDate() + userData.daysRemaining);

        // we need the internal property of the token to determine if the user logged in from the intranet
        const jwtDecoded = jwtDecode(loginResponse.jwt);
        const internal = !!jwtDecoded.internal;

        // do not send ChurnZero data if the user is internal
        if (!internal && userData.sfAccountId && userData.sfContactId) {
          setChurnZeroData(userData, hsVersion);
        }

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          loginResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        ).then(() => {
          segmentIdentifyGroup(userData, hsVersion).then(() => {
            window.analytics.track("User Login", {});
            if (userData && userData.isFreeTrial) {
              trackFTUserAssignedCTA(
                configCatObject.ftWelcomeBannerCtaButtonText || "Try Our Tests"
              );
            }
          });
        });

        // set JWT to localStorage for Criteria Platform scope
        yield localStorage.setItem("token", loginResponse.jwt);

        // if logged in user has access to both Hireselect and Develop, we need to set the cross-subdomain subtoken here
        // so that if they click an external link to Develop (ex. email link) they will be directed to the Develop dashboard properly.
        if (hasPosthireAccess && configCatObject.isTeamsEnabled) {
          Cookies.set("critToken", loginResponse.jwt, {
            path: "/",
            domain:
              new URL(developByCriteriaURL).hostname === "localhost"
                ? "localhost"
                : ".criteriacorp.com",
          });
        }

        yield put(getRoles());

        if (userData.daysRemaining > 0) {
          yield put(getCompanyInfo());
        } else {
          yield put(getCompanyInfo(true));
        }
      }

      yield put(loginSuccess(loginResponse.jwt, userData, configCatObject));

      // set or remove cookie for rememberMe option
      if (data.formData.rememberMe) {
        Cookies.set(
          "_Secure-cog-user",
          {
            emailAddress: data.formData.emailAddress,
          },
          { expires: 30, path: "/", domain: ".criteriacorp.com" }
        );
      } else {
        Cookies.remove("_Secure-cog-user");
      }

      if (userData.locale === "AU") {
        yield put(updateSourcesByLocale());
      }

      const queryParams = queryString.parse(window.location.search);

      const verifyToken =
        window.location.pathname === "/verifyAccount" &&
        queryParams.verifyToken;

      if (verifyToken) {
        yield put(push("/"));
      }

      return;
    }

    if (loginResponse.jwt && data.liOnboarding) {
      // TODO: check linkedin JWT for decouple project loginResponse.jwt
      // LinkedIn Integration Login Flow
      const userData = loginResponse.data;

      if (userData.hsVersion !== 0 && userData.hsVersion !== 1) {
        yield put(
          showLinkedInAuthorizeModal(loginResponse.jwt, userData.emailAddress)
        );
        return;
      }
      const liOnboardingDataCopy = cloneDeep(data.liOnboardingIntegrationData);

      liOnboardingDataCopy.userConfirmation = false;

      yield put(
        authorizeLinkedInIntegration(
          loginResponse.jwt,
          userData.emailAddress,
          liOnboardingDataCopy
        )
      );
    }
  } catch (err) {
    let errorCode;

    yield err.response.json().then((response) => {
      errorCode = response.error;
    });

    yield put(loginError(errorCode));

    if (data.liOnboarding && errorCode === 1) {
      const liOnboardingRequestFailureUrl = `${personalUrl}linkedinAts/integrate/failure`;

      yield call(request, liOnboardingRequestFailureUrl, {
        method: "post",
        body: JSON.stringify(data.liOnboardingIntegrationData),
      });
    }
  }
}

/* ----------- LinkedIn Integration ----------- */
export function* onAuthorizeLinkedInIntegration() {
  const watch = yield takeLatest(
    AUTHORIZE_LINKEDIN_INTEGRATION,
    authorizeLinkedInIntegrationSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);

  yield cancel(watch);
}
export function* authorizeLinkedInIntegrationSaga(data) {
  const liOnboardingRequestUrl = `${personalUrl}linkedinAts/integrate`;

  try {
    yield call(request, liOnboardingRequestUrl, {
      method: "post",
      headers: {
        Authorization: data.token,
      },
      body: JSON.stringify(data.liOnboardingIntegrationData),
    });

    window.location.href = data.liOnboardingIntegrationData.redirectUrl;
  } catch (err) {
    let errorMessage;

    yield put(getLoginTokenLinkedIn(data.token));

    yield err.response.json().then((response) => {
      errorMessage = response;
    });

    yield put(linkedInIntegrationError(data.userEmail, errorMessage));
  }
}

/* ----------- Partners Login ----------- */

export function* partnersLogin() {
  const watch = yield takeLatest(PARTNERS_LOGIN, partnersLoginSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}
export function* partnersLoginSaga(data) {
  const requestURL = `${partnersCenterUrl}login`;
  try {
    const partnersLoginResponse = yield call(request, requestURL, {
      method: "post",
      body: JSON.stringify(data.formData),
    });
    if (partnersLoginResponse.jwt) {
      localStorage.setItem("partnersToken", partnersLoginResponse.jwt);
    }
    yield put(
      partnersLoginSuccess(
        partnersLoginResponse.jwt,
        partnersLoginResponse.data
      )
    );
  } catch (err) {
    let errorCode;
    yield err.response.json().then((response) => {
      errorCode = response.error;
    });
    yield put(partnersLoginError(errorCode));
  }
}

/* ----------- Partners Forgot Password ----------- */

export function* partnersForgotPassword() {
  const watch = yield takeLatest(
    PARTNERS_FORGOT_PASSWORD,
    partnersForgotPasswordSaga
  );
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* partnersForgotPasswordSaga(data) {
  const requestURL = `${partnersCenterUrl}resetPassword`;
  try {
    const forgotPasswordResponse = yield call(request, requestURL, {
      method: "put",
      body: JSON.stringify(data),
    });
    yield put(partnersForgotPasswordSuccess(forgotPasswordResponse));
  } catch (err) {
    yield put(
      partnersForgotPasswordError(
        "A user with this email address does not exist. Please check your entry and try again."
      )
    );
  }
}

/* ----------- Reset Password ----------- */

export function* resetPassword() {
  const watch = yield takeLatest(RESET_PASSWORD, resetPasswordSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// Makes API call to get the search results, in response to the SUBMIT_SEARCH action
export function* resetPasswordSaga(data) {
  const requestURL = `${personalUrl}resetPasswordLink`;
  try {
    const resetPasswordResponse = yield call(request, requestURL, {
      method: "put",
      body: JSON.stringify(data),
    });
    yield put(resetPasswordSuccess(resetPasswordResponse));
  } catch (err) {
    yield put(resetPasswordError(err));
  }
}

/* ----------- Get Invoice ----------- */

export function* getInvoice() {
  const watch = yield takeLatest(GET_INVOICE, getInvoiceSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// Makes API call to get the search results, in response to the SUBMIT_SEARCH action
export function* getInvoiceSaga(data) {
  const requestURL = `${personalUrl}invoice/${data.invoiceNumber}`;
  try {
    const getInvoiceResponse = yield call(request, requestURL, {
      method: "get",
    });
    const downloadLink = document.getElementById("invoice");
    downloadLink.href = `data:application/pdf;base64,${getInvoiceResponse.data[0]}`;
    downloadLink.click();
    yield put(getInvoiceSuccess(getInvoiceResponse));
  } catch (err) {
    yield put(getInvoiceError(err));
  }
}

/* ----------- Get Invoice Pricing ----------- */

export function* getInvoicePricing() {
  const watch = yield takeLatest(GET_INVOICE_PRICING, getInvoicePricingSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* getInvoicePricingSaga(data) {
  const requestURL = `${personalUrl}payInvoice/${data.invoiceNumber}`;
  try {
    const getInvoicePricingResponse = yield call(request, requestURL, {
      method: "get",
    });
    yield put(getInvoicePricingSuccess(getInvoicePricingResponse.data.pricing));
  } catch (err) {
    yield put(getInvoicePricingError(err));
  }
}

/* ----------- Get Company info ----------- */
export function* onGetCompanyInfo() {
  yield takeLatest(GET_COMPANY_INFO, getCompanyInfoSaga);
  // cancel the watch when location changes
  // yield take(LOCATION_CHANGE);
  // yield cancel(watch);
}

export function* getCompanyInfoSaga(action) {
  const requestURL = `${personalUrl}company`;
  try {
    const getCompanyInfoResponse = yield call(request, requestURL, {
      method: "get",
    });
    if (action.redirect) {
      yield put(getCompanyInfoSuccess(getCompanyInfoResponse));
      yield put(push("/subscribe"));
    } else {
      const hsVersion = getVersionName(getCompanyInfoResponse.hsVersion);

      if (getCompanyInfoResponse.sfAccountId) {
        window.ChurnZero.push([
          "setAttribute",
          "account",
          {
            SoftwareVersion: hsVersion,
            AccountExternalId: getCompanyInfoResponse.sfAccountId,
          },
        ]);
        window.ChurnZero.push([
          "setAttribute",
          "contact",
          {
            SoftwareVersion: hsVersion,
          },
        ]);
      }
      yield put(getCompanyInfoSuccess(getCompanyInfoResponse));
    }
  } catch (err) {
    yield put(getCompanyInfoError(err));
  }
}

// --------- get roles -----------
export function* getRolesSaga() {
  const rolesRequestURL = `${personalUrl}roles`;
  try {
    const rolesResponse = yield call(request, rolesRequestURL, {
      method: "get",
    });
    if (rolesResponse.status === 204) {
      yield put(getRolesSuccess({}));
    } else {
      const rolesObj = {};
      rolesResponse.data.forEach((role) => {
        rolesObj[role.roleId] = role;
      });
      yield put(getRolesSuccess(rolesObj));
    }
  } catch (err) {
    yield put(getRolesError(err));
  }
}

export function* roles() {
  yield takeLatest(GET_ROLES, getRolesSaga);

  // cancel the watch when location changes
  // yield take(LOCATION_CHANGE);
  // yield cancel(watch);
}

/* ----------- Get Billing Info ----------- */

export function* getBillingInfo() {
  yield takeLatest(GET_BILLING_INFO, getBillingInfoSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  // yield cancel(watch);
}
export function* getBillingInfoSaga(data) {
  const requestURL = `${personalUrl}billingInfo/${data.userAccountId}`;
  try {
    const getBillingInfoResponse = yield call(request, requestURL, {
      method: "get",
    });
    yield put(getBillingInfoSuccess(getBillingInfoResponse.data));
  } catch (err) {
    yield put(getBillingInfoError(err));
  }
}

/* ----------- Set New Password----------- */

export function* setNewPassword() {
  yield takeLatest(SET_NEW_PASSWORD, setNewPasswordSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  // yield cancel(watch);
}
export function* setNewPasswordSaga(data) {
  // Check for presence of jwt token to determined logged in status
  const requestURL = `${personalUrl}doResetPasswordLink`;
  const { tk1, tk2, password } = data;

  try {
    const setNewPasswordResponse = yield call(request, requestURL, {
      method: "put",
      body: JSON.stringify({ tk1, tk2, password }),
    });

    yield put(setNewPasswordSuccess());

    if (setNewPasswordResponse.jwt && setNewPasswordResponse.data) {
      const userData = setNewPasswordResponse.data;
      const hasHireSelectAccess = userData.hireselectAccess;
      const hasPosthireAccess = checkIfUserHasPosthireAccess(userData);
      const hasDevelopByCriteriaAccess = userData.developByCriteriaAccess;
      const hsVersion = getVersionName(userData.hsVersion);

      const configCatObject = yield getConfigCatObject(userData);

      yield put(setupFeatureFlag(configCatObject));

      if (
        !hasDevelopByCriteriaAccess &&
        !hasHireSelectAccess &&
        !hasPosthireAccess
      ) {
        yield put(loginError(6)); // errorCode 6 means that the user does not have access to the prehire or posthire platform
        return;
      }

      if (hasDevelopByCriteriaAccess && !hasHireSelectAccess) {
        Cookies.set("critToken", setNewPasswordResponse.jwt, {
          path: "/",
          domain:
            new URL(developByCriteriaURL).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          setNewPasswordResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.location.href = developByCriteriaURL;
        return;
      }

      if (
        hasPosthireAccess &&
        !hasHireSelectAccess &&
        !hasDevelopByCriteriaAccess
      ) {
        Cookies.set("critToken", setNewPasswordResponse.jwt, {
          path: "/",
          domain:
            new URL(teamsUrl).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          setNewPasswordResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.analytics.track(
          "talent insights login",
          {
            "referral source type": "url",
            url: teamsUrl,
          },
          () => {
            window.location.href = teamsUrl;
          }
        );
      }

      if (hasHireSelectAccess) {
        const dateToday = new Date();
        const dateExpired = new Date(dateToday);

        dateExpired.setDate(dateExpired.getDate() + userData.daysRemaining);

        // we need the internal property of the token to determine if the user logged in from the intranet
        const jwtDecoded = jwtDecode(setNewPasswordResponse.jwt);
        const internal = !!jwtDecoded.internal;

        // do not send ChurnZero data if the user is internal
        if (!internal && userData.sfAccountId && userData.sfContactId) {
          setChurnZeroData(userData, hsVersion);
        }

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          setNewPasswordResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        ).then(() => {
          segmentIdentifyGroup(userData, hsVersion).then(() => {
            window.analytics.track("User Login", {});
            if (userData && userData.isFreeTrial) {
              trackFTUserAssignedCTA(
                configCatObject.ftWelcomeBannerCtaButtonText || "Try Our Tests"
              );
            }
          });
        });

        // set JWT to localStorage for Criteria Platform scope
        yield localStorage.setItem("token", setNewPasswordResponse.jwt);

        // if logged in user has access to both Hireselect and Develop, we need to set the cross-subdomain subtoken here
        // so that if they click an external link to Develop (ex. email link) they will be directed to the Develop dashboard properly.
        if (hasPosthireAccess && configCatObject.isTeamsEnabled) {
          Cookies.set("critToken", setNewPasswordResponse.jwt, {
            path: "/",
            domain:
              new URL(developByCriteriaURL).hostname === "localhost"
                ? "localhost"
                : ".criteriacorp.com",
          });
        }

        yield put(getRoles());

        if (userData.daysRemaining > 0) {
          yield put(getCompanyInfo());
        } else {
          yield put(getCompanyInfo(true));
        }
      }

      yield put(
        loginSuccess(setNewPasswordResponse.jwt, userData, configCatObject)
      );

      if (userData.locale === "AU") {
        yield put(updateSourcesByLocale());
      }

      yield put(push("/"));
    }
  } catch (err) {
    yield put(setNewPasswordError(err));
  }
}

/* ----------- Partners Set New Password----------- */

export function* setNewPasswordPartners() {
  yield takeLatest(PARTNERS_SET_NEW_PASSWORD, setNewPasswordPartnersSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  // yield cancel(watch);
}
export function* setNewPasswordPartnersSaga(data) {
  // Check for presence of jwt token to determined logged in status
  const requestURL = `${partnersCenterUrl}doResetPasswordLink`;
  try {
    yield call(request, requestURL, {
      method: "put",
      body: JSON.stringify(data),
    });
    yield put(setNewPasswordPartnersSuccess());
    yield put(push("/partners"));
  } catch (err) {
    yield put(setNewPasswordPartnersError(err));
  }
}

/* ---------- get users ------------ */

export function* getUsers() {
  const usersRequestURL = `${personalUrl}users`;
  try {
    const usersResponse = yield call(request, usersRequestURL, {
      method: "get",
    });
    if (usersResponse.status === 204) {
      yield put(getUsersSuccess({}));
    }
    const activeUsers = {};
    const inactiveUsers = {};
    usersResponse.data.forEach((user) => {
      if (
        (user.userDeleted === 0 || user.userDeleted === "0") &&
        !user.suspendedByUserAccountId
      ) {
        activeUsers[user.userAccountId] = user;
        activeUsers[user.userAccountId].fullName =
          `${user.firstName} ${user.lastName}`;
      } else if (user.suspendedByUserAccountId) {
        inactiveUsers[user.userAccountId] = user;
        inactiveUsers[user.userAccountId].fullName =
          `${user.firstName} ${user.lastName}`;
      }
    });
    yield put(getUsersSuccess(activeUsers, inactiveUsers));
  } catch (err) {
    yield put(getUsersError(err));
  }
}

export function* users() {
  const watch = yield takeLatest(GET_USERS, getUsers);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

/* ----------- Get Sources ----------- */
export function* getSources() {
  const watch = yield takeLatest(GET_SOURCES, getSourcesSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}
export function* getSourcesSaga() {
  const getSourcesURL = `${personalUrl}candidateSources`;
  try {
    const getSourcesResponse = yield call(request, getSourcesURL, {
      method: "get",
    });
    yield put(getSourcesSuccess(getSourcesResponse.data));
  } catch (err) {
    yield put(getSourcesError(err));
  }
}

/* ----------- create source ----------- */

export function* createSource() {
  const watch = yield takeLatest(CREATE_SOURCE, createSourceSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}
export function* createSourceSaga(action) {
  const createSourceURL = `${personalUrl}jobs/${action.jobId}/links`;
  try {
    const createSourceResponse = yield call(request, createSourceURL, {
      method: "post",
      body: JSON.stringify({ sourceName: action.sourceName }),
    });
    yield put(createSourceSuccess(createSourceResponse.data[0]));
  } catch (err) {
    yield put(createSourceError(err));
  }
}

// --------- Get identity provider -----------
export function* getIdentityProviderSaga(action) {
  const { formData /* rememberMe */ } = action;
  const getIdentityProviderRequestURL = `${personalUrl}identityProvider`;
  try {
    const getIdentityProviderResponse = yield call(
      request,
      getIdentityProviderRequestURL,
      {
        method: "post",
        body: JSON.stringify(formData),
      }
    );
    // if the company has mfa enabled and sso off, and is not logged-in (does not have a valid token)
    // directly push the user to the mfa log in screen
    // if (
    //   getIdentityProviderResponse &&
    //   getIdentityProviderResponse.identityProviderMode === 0 &&
    //   getIdentityProviderResponse.mfaEnabled === 2 &&
    //   getIdentityProviderResponse.auth0 &&
    //   getIdentityProviderResponse.auth0.mfa &&
    //   getIdentityProviderResponse.auth0.mfa.redirectUri &&
    //   isTokenExpired()
    // ) {
    //   const { ranDoCd, verifier, challenge } = getSecuredLoginParams(
    //     formData.emailAddress
    //   );

    //   // save to session storage
    //   sessionStorage.setItem('logState', ranDoCd);
    //   sessionStorage.setItem('logVerif', verifier);
    //   sessionStorage.setItem('rememberMe', rememberMe);
    //   sessionStorage.setItem('loginType', 'mfa');

    //   window.location.href = getIdpLoginRedirectUrl(
    //     fromJS(getIdentityProviderResponse),
    //     ranDoCd,
    //     challenge,
    //     'mfa',
    //     formData.emailAddress
    //   );
    // } else {
    //   yield put(getIdentityProviderSuccess(getIdentityProviderResponse));
    // }
    yield put(getIdentityProviderSuccess(getIdentityProviderResponse));
  } catch (err) {
    yield put(getIdentityProviderError(err));
  }
}

export function* getIdentityProvider() {
  const watch = yield takeLatest(
    GET_IDENTITY_PROVIDER,
    getIdentityProviderSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Sso login -----------
export function* ssoLoginSaga(action) {
  // const ssoLoginRequestURL = `${personalUrl}login/sso${
  //   action.isAuth0 ? '/auth0' : ''
  //   }`;
  const ssoLoginRequestURL = `${personalUrl}login/sso`;

  try {
    const ssoLoginResponse = yield call(request, ssoLoginRequestURL, {
      method: "post",
      body: JSON.stringify(action.formData),
      credentials: "same-origin",
    });

    if (ssoLoginResponse.jwt && ssoLoginResponse.data) {
      // we need to determine if the logged in user has access to hireselect, teaming, or both, so
      // we can redirect them appropriately.
      const userData = ssoLoginResponse.data;
      const hasHireSelectAccess = userData.hireselectAccess;
      const hasTeamingAccess = checkIfUserHasPosthireAccess(userData);
      const hsVersion = getVersionName(userData.hsVersion);
      const configCatObject = yield getConfigCatObject(userData);
      yield put(setupFeatureFlag(configCatObject));

      // if the logged in user only has access to teaming, we set a cross-subdomain token here
      // and then redirect them directly to teaming.  if they have access to hireselect, we go
      // ahead with the normal hireselect login procedure.
      if (
        hasTeamingAccess &&
        !hasHireSelectAccess &&
        configCatObject.isTeamsEnabled
      ) {
        // If develop only user we need to redirect to develop by criteria page
        if (userData.developByCriteriaAccess) {
          return signInUserIntoDevelopViaSSO(ssoLoginResponse.jwt);
        }

        Cookies.set("critToken", ssoLoginResponse.jwt, {
          path: "/",
          domain: ".criteriacorp.com",
        });
        // delete sessionStorage items
        sessionStorage.removeItem("logState");
        sessionStorage.removeItem("logVerif");
        sessionStorage.removeItem("rememberMe");

        // re-query for idp info
        const idpInfo = yield call(request, `${personalUrl}identityProvider`, {
          method: "post",
          body: JSON.stringify({
            emailAddress: userData.emailAddress,
          }),
        });

        // set or remove cookie for rememberMe option
        handleRememberMe(action.formData.rememberMe, userData.emailAddress);

        // logout of sso session
        const logoutUrl = getIdpLogoutUrl(
          idpInfo,
          "teaming"
          // sessionStorage.getItem('loginType')
        );

        yield call(() => {
          trackSSOLogin(
            userData,
            ssoLoginResponse.jwt,
            hsVersion,
            configCatObject.ftDashboardExperimentAllocation,
            true,
            logoutUrl
          ); // will also handle redirect
        });
        return;
      }

      // If the user logged in via develop, isPostHireLogin payload is set to true.
      // if isPostHireLogin is true we need to redirect the user to the develop by criteria page
      if (userData.developByCriteriaAccess && action.isPostHireLogin) {
        return signInUserIntoDevelopViaSSO(ssoLoginResponse.jwt);
      }

      const dateToday = new Date();
      const dateExpired = new Date(dateToday);
      dateExpired.setDate(dateExpired.getDate() + userData.daysRemaining);

      // we need the internal property of the token to determine if the user logged in from the intranet
      const jwtDecoded = jwtDecode(ssoLoginResponse.jwt);
      const internal = !!jwtDecoded.internal;

      if (!internal) {
        setChurnZeroData(userData, hsVersion);
      }

      trackSSOLogin(
        userData,
        ssoLoginResponse.jwt,
        hsVersion,
        configCatObject.ftDashboardExperimentAllocation,
        false
      );

      yield localStorage.setItem("token", ssoLoginResponse.jwt);
      yield put(ssoLoginSuccess(userData, configCatObject));

      // if logged in user has access to both Hireselect and TI, we need to set the cross-subdomain subtoken here
      // so that if they click an external link to TI (ex. email link) they will be directed to the TI dashboard properly.
      if (hasTeamingAccess && configCatObject.isTeamsEnabled) {
        Cookies.set("critToken", ssoLoginResponse.jwt, {
          path: "/",
          domain:
            new URL(teamsUrl).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });
      }

      yield put(getRoles());

      if (userData.daysRemaining > 0) {
        yield put(getCompanyInfo());
      } else {
        yield put(getCompanyInfo(true));
      }

      // delete sessionStorage items
      sessionStorage.removeItem("logState");
      sessionStorage.removeItem("logVerif");
      sessionStorage.removeItem("rememberMe");

      // set or cookie for rememberMe option
      handleRememberMe(action.formData.rememberMe, userData.emailAddress);

      Cookies.set("ssoLoginSuccess", true);

      // deep linking for SSO login - redirect users to the page they were trying to access
      window.location.href = sessionStorage.getItem("loginPath");
      sessionStorage.removeItem("loginPath");
    }
  } catch (err) {
    let errorCode;
    yield err.response.json().then((response) => {
      errorCode = response.error ? response.error : response;
    });
    yield put(ssoLoginError(errorCode));

    const queryParams = queryString.parse(window.location.search);
    const state = decodeURIComponent(queryParams.state.replace(/\s/, "+")); // this is encoding the +
    // logout of cognito sso session when ssoLogin errors out.
    if (!state.includes("cognitoSignOut")) {
      sessionStorage.setItem("logState", `${state}+cognitoSignOut`);
      const email = state ? state.split("+")[1] : false; // there is no + to split
      let logoutUrl = "?";
      Object.keys(queryParams).forEach((paramKey, i) => {
        if (paramKey === "state") {
          logoutUrl = `${logoutUrl}${
            i !== 0 ? "&" : ""
          }${paramKey}=${state}+cognitoSignOut`;
        } else {
          logoutUrl = `${logoutUrl}${i !== 0 ? "&" : ""}${paramKey}=${
            queryParams[paramKey]
          }`;
        }
      });
      logoutUrl = `${window.location.href.split("?")[0]}${encodeURIComponent(
        logoutUrl
      )}`;

      const idpInfo = yield call(request, `${personalUrl}identityProvider`, {
        method: "post",
        body: JSON.stringify({
          emailAddress: email,
        }),
      });
      const cognitoLogoutUrl = `${idpInfo.cognitoBaseUrl.slice(
        0,
        -16
      )}logout?client_id=${idpInfo.clientId}&logout_uri=${
        window.location.href.split("?")[0]
      }`;
      // open cognito logout in a different window
      const cognitoLogoutWindow = window.open(cognitoLogoutUrl);
      // blur the new window and focus on original window (this may not work on all browsers)
      cognitoLogoutWindow.blur();
      window.focus();
      // close new window after it has time to logout of cognito
      setTimeout(() => cognitoLogoutWindow.close(), 500);
    }
    // delete sessionStorage items
    sessionStorage.removeItem("logState");
    sessionStorage.removeItem("logVerif");
    sessionStorage.removeItem("rememberMe");
  }
}

export function* ssoLogin() {
  const watch = yield takeLatest(SSO_LOGIN, ssoLoginSaga);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

/* ---------- Update First Teams Login ----------- */
export function* updateFirstTeamsLogin() {
  const watch = yield takeLatest(
    UPDATE_FIRST_TEAMS_LOGIN,
    updateFirstTeamsLoginSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}
export function* updateFirstTeamsLoginSaga() {
  const requestURL = `${personalUrl}teaming/users/firstTimeLogin`;
  try {
    const updateFirstTeamsLoginResponse = yield call(request, requestURL, {
      method: "PUT",
    });

    yield put(updateFirstTeamsLoginSuccess(updateFirstTeamsLoginResponse));
  } catch (err) {
    yield put(updateFirstTeamsLoginError(err));
  }
}

/* ---------- Delete Candidate ---------- */

export function* deleteCandidateSaga(action) {
  const requestURL = `${personalUrl}anonymizeCandidates`;
  const formData = {
    testTakerIds: [Number(action.currentCandidateId)],
  };

  try {
    const deleteCandidateResponse = yield call(request, requestURL, {
      method: "POST",
      body: JSON.stringify(formData),
    });

    if (deleteCandidateResponse !== null) {
      // if delete from Results, need to update candidate counts and candidates to display in the view
      if (
        action.deleteLocation === "Results" &&
        action.jobId &&
        action.currentStageId
      ) {
        yield put(getCandidateCounts(action.jobId));
        yield put(
          updateCandidatesAfterDelete(
            action.currentStageId,
            action.currentCandidateId
          )
        );
      }
      yield put(deleteCandidateSuccess(action.currentCandidateId));
    }
  } catch (err) {
    yield put(deleteCandidateError(err));
  }
}

export function* deleteCandidate() {
  const watch = yield takeLatest(DELETE_CANDIDATE, deleteCandidateSaga);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* refreshUserData() {
  const watch = yield takeLatest(REFRESH_USER_DATA, refreshUserDataSaga);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* refreshUserDataSaga() {
  const requestURL = `${personalUrl}me`;

  try {
    const response = yield call(request, requestURL, {
      method: "GET",
    });
    if (response) {
      // need to get intranet info from JWT and set to redux store before moving on because api doesn't know about intranet scope.
      const jwtDecoded = jwtDecode(localStorage.getItem("token"));
      const { internal, internalMode } = yield jwtDecoded;
      if (internal) {
        response.internal = internal;
      }
      if (internalMode) {
        response.internalMode = internalMode;
      }

      yield put(refreshUserDataSuccess(response));
    }
  } catch (err) {
    yield put(refreshUserDataError(err));
  }
}

/* ---------- Get Dynamic Score Reports Data ----------- */
export function* getDynamicScoreReportsDataSaga(action) {
  const {
    testEventId,
    testId,
    format,
    reportType,
    testName,
    second,
    source,
    userAccountId,
  } = action;
  let requestURL = `${personalUrl}webReports/${testEventId}/`;

  if (testId) {
    requestURL +=
      String(testId).substring(0, 2) === "TM"
        ? `tmTests/${testId.substring(2)}`
        : `tests/${testId}`;
  } else if (reportType === "Workplace Insights Report") {
    requestURL += "workplaceInsights";
  } else {
    requestURL += "summary";
  }
  if (format) {
    requestURL += `?format=${format}`;
  }

  const reportTkn = Cookies.get(`dynamicScoreReportsTkn_${testEventId}`);
  try {
    const dynamicScoreReportsDataResponse = yield call(
      request,
      requestURL,
      {
        method: "GET",
      },
      reportTkn
    );

    if (dynamicScoreReportsDataResponse.status === 204) {
      yield put(
        getDynamicScoreReportsDataSuccess(
          {},
          testEventId,
          source,
          userAccountId
        )
      );
    }

    if (format === "pdf") {
      // eslint-disable-next-line no-nested-ternary
      const pdfTitle = testName
        ? `-${testName}-${testEventId}`
        : reportType === "Workplace Insights Report"
          ? "-Workplace-Insights.pdf"
          : "-Candidate-Summary.pdf";

      // eslint-disable-next-line no-nested-ternary
      const responseData = testId
        ? second
          ? dynamicScoreReportsDataResponse.data[1]
          : dynamicScoreReportsDataResponse.data[0]
        : dynamicScoreReportsDataResponse.data;

      if (window.navigator.msSaveOrOpenBlob) {
        // for IE and Edge
        const sliceSize = 1024;
        const byteCharacters = atob(responseData);
        const bytesLength = byteCharacters.length;
        const slicesCount = Math.ceil(bytesLength / sliceSize);
        const byteArrays = new Array(slicesCount);
        // eslint-disable-next-line no-plusplus
        for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
          const begin = sliceIndex * sliceSize;
          const end = Math.min(begin + sliceSize, bytesLength);

          const bytes = new Array(end - begin);
          // eslint-disable-next-line no-plusplus
          for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
            bytes[i] = byteCharacters[offset].charCodeAt(0);
          }
          byteArrays[sliceIndex] = new Uint8Array(bytes);
        }
        const blob = new Blob(byteArrays, {
          type: "application/pdf",
        });
        window.navigator.msSaveOrOpenBlob(blob, pdfTitle);
      } else {
        const dlnk = document.getElementById(
          // eslint-disable-next-line no-nested-ternary
          testId
            ? `testReportDwnldLnk`
            : reportType === "Workplace Insights Report"
              ? "workplaceInsightsDwnldLnk"
              : "summaryDwnldLnk"
        );
        const dlnkDownloadBefore = dlnk.download;
        dlnk.download += pdfTitle;

        dlnk.href = `data:application/pdf;base64,${responseData}`;
        dlnk.click();
        dlnk.download = dlnkDownloadBefore;
      }
    } else {
      if (dynamicScoreReportsDataResponse.data) {
        const userData = {
          userAccountId,
          companyAccountId:
            dynamicScoreReportsDataResponse.data.testEvent.company,
          locale:
            dynamicScoreReportsDataResponse.data.testEvent.candidate.locale,
        };

        const configCatObject = yield getConfigCatObject(userData);
        yield put(setupFeatureFlag(configCatObject));
      }

      yield put(
        getDynamicScoreReportsDataSuccess(
          dynamicScoreReportsDataResponse.data,
          testEventId,
          source,
          userAccountId
        )
      );
    }
  } catch (err) {
    let errorMsg = "There was an error retrieving this web report.";

    if (err.response.status === 403) {
      errorMsg =
        "You do not have permission to access the web report for this test event.";
    } else if (err.response.status === 401) {
      errorMsg =
        "Access has expired. Please navigate to this web report using the link you originally received.";
    }

    yield put(getDynamicScoreReportsDataError(errorMsg));
  }
}

export function* getDynamicScoreReportsData() {
  const watch = yield takeLatest(
    GET_DYNAMIC_SCORE_REPORTS_DATA,
    getDynamicScoreReportsDataSaga
  );
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

/* ---------- Get Work Styles Report for Dynamic Score Reports ----------- */
export function* getDynamicScoreReportsWorkStylesSaga(action) {
  const { testEventId, format } = action;

  const requestURL = `${personalUrl}webReports/${testEventId}/workStyles?format=${format}`;

  const reportTkn = Cookies.get(`dynamicScoreReportsTkn_${testEventId}`);
  try {
    const getDynamicScoreReportsWorkStylesResponse = yield call(
      request,
      requestURL,
      {
        method: "GET",
      },
      reportTkn
    );

    if (format === "pdf") {
      if (window.navigator.msSaveOrOpenBlob) {
        // for IE and Edge
        const sliceSize = 1024;
        const byteCharacters = atob(getDynamicScoreReportsWorkStylesResponse);
        const bytesLength = byteCharacters.length;
        const slicesCount = Math.ceil(bytesLength / sliceSize);
        const byteArrays = new Array(slicesCount);
        // eslint-disable-next-line no-plusplus
        for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
          const begin = sliceIndex * sliceSize;
          const end = Math.min(begin + sliceSize, bytesLength);

          const bytes = new Array(end - begin);
          // eslint-disable-next-line no-plusplus
          for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
            bytes[i] = byteCharacters[offset].charCodeAt(0);
          }
          byteArrays[sliceIndex] = new Uint8Array(bytes);
        }
        const blob = new Blob(byteArrays, { type: "application/pdf" });
        window.navigator.msSaveOrOpenBlob(blob, "-Work-Styles.pdf");
      } else {
        const dlnk = document.getElementById("workStylesDwnldLnk");
        const dlnkDownloadBefore = dlnk.download;
        // eslint-disable-next-line no-nested-ternary
        dlnk.download += "-Work-Styles.pdf";
        dlnk.href = `data:application/pdf;base64,${getDynamicScoreReportsWorkStylesResponse.data}`;
        dlnk.click();
        dlnk.download = dlnkDownloadBefore;
      }
    } else {
      yield put(
        getDynamicScoreReportsWorkStylesSuccess(
          getDynamicScoreReportsWorkStylesResponse.data
        )
      );
    }
  } catch (err) {
    yield put(getDynamicScoreReportsWorkStylesError(err));
  }
}

export function* getDynamicScoreReportsWorkStyles() {
  const watch = yield takeLatest(
    GET_DYNAMIC_SCORE_REPORTS_WORK_STYLES,
    getDynamicScoreReportsWorkStylesSaga
  );
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

/* ----------- Get Test Batteries ----------- */

export function* getTestBatteries() {
  const watch = yield takeLatest(GET_TEST_BATTERIES, getTestBatteriesSaga);
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* getTestBatteriesSaga() {
  const requestURL = `${personalUrl}testBatteries`;
  try {
    const getTestBatteriesResponse = yield call(request, requestURL, {
      method: "get",
    });
    if (getTestBatteriesResponse.status === 204) {
      yield put(getTestBatteriesSuccess({}));
    } else {
      yield put(getTestBatteriesSuccess(getTestBatteriesResponse.data));
    }
  } catch (err) {
    yield put(getTestBatteriesError(err));
  }
}

// --------- Send Support Bot Question -----------
export function* sendSupportBotQuestionSaga(action) {
  const sendSupportBotQuestionRequestURL = `${personalUrl}criteriaChat/submitQuestion`;
  try {
    const supportBotResponse = yield call(
      request,
      sendSupportBotQuestionRequestURL,
      {
        method: "post",
        body: JSON.stringify(action.formData),
      }
    );

    yield put(sendSupportBotQuestionSuccess(supportBotResponse));
  } catch (err) {
    yield put(sendSupportBotQuestionError(err));
  }
}

export function* sendSupportBotQuestion() {
  const watch = yield takeLatest(
    SEND_SUPPORT_BOT_QUESTION,
    sendSupportBotQuestionSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Send Support Bot Response Feedback -----------
export function* sendResponseFeedbackSaga(action) {
  const sendResponseFeedbackRequestURL = `${personalUrl}criteriaChat/provideFeedbackOnAnswer`;

  try {
    yield call(request, sendResponseFeedbackRequestURL, {
      method: "post",
      body: JSON.stringify(action.formData),
    });

    yield put(sendResponseFeedbackSuccess());
  } catch (err) {
    yield put(sendResponseFeedbackError(err));
  }
}

export function* sendResponseFeedback() {
  const watch = yield takeLatest(
    SEND_RESPONSE_FEEDBACK,
    sendResponseFeedbackSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get current candidate analytics -----------
export function* getCandidateAnalyticsSaga(action) {
  const getCandidateAnalyticsRequestURL = `${personalUrl}snapshotAnalytics/candidateAnalytics`;
  try {
    const { dateFilter, startDate, endDate } = action;

    const additionalData =
      dateFilter === "custom" ? { startDate, endDate } : {};

    const getCandidateAnalyticsResponse = yield call(
      request,
      getCandidateAnalyticsRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter, ...additionalData }),
      }
    );
    if (getCandidateAnalyticsResponse.status === 204) {
      yield put(getCandidateAnalyticsSuccess(null));
    }
    yield put(
      getCandidateAnalyticsSuccess(
        getCandidateAnalyticsResponse.data,
        dateFilter,
        "total"
      )
    );
  } catch (err) {
    yield put(getCandidateAnalyticsError(err));
  }
}

export function* getCandidateAnalytics() {
  const watch = yield takeLatest(
    GET_CANDIDATE_ANALYTICS,
    getCandidateAnalyticsSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get previous candidate analytics -----------
export function* getPrevCandidateAnalyticsSaga(action) {
  const getPrevCandidateAnalyticsRequestURL = `${personalUrl}snapshotAnalytics/candidateAnalyticsPrev`;
  try {
    const getPrevCandidateAnalyticsResponse = yield call(
      request,
      getPrevCandidateAnalyticsRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter: action.dateFilter }),
      }
    );
    if (getPrevCandidateAnalyticsResponse.status === 204) {
      yield put(getPrevCandidateAnalyticsSuccess(null));
    }
    yield put(
      getPrevCandidateAnalyticsSuccess(
        getPrevCandidateAnalyticsResponse.data,
        action.dateFilter,
        "prevTotal"
      )
    );
  } catch (err) {
    yield put(getPrevCandidateAnalyticsError(err));
  }
}

export function* getPrevCandidateAnalytics() {
  const watch = yield takeLatest(
    GET_PREV_CANDIDATE_ANALYTICS,
    getPrevCandidateAnalyticsSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get current assessment analytics -----------
export function* getAssessmentAnalyticsSaga(action) {
  const getAssessmentAnalyticsRequestURL = `${personalUrl}snapshotAnalytics/assessmentAnalytics`;
  try {
    const { dateFilter, startDate, endDate } = action;

    const additionalData =
      dateFilter === "custom" ? { startDate, endDate } : {};

    const getAssessmentAnalyticsResponse = yield call(
      request,
      getAssessmentAnalyticsRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter, ...additionalData }),
      }
    );
    if (getAssessmentAnalyticsResponse.status === 204) {
      yield put(getAssessmentAnalyticsSuccess(null));
    }
    yield put(
      getAssessmentAnalyticsSuccess(
        getAssessmentAnalyticsResponse.data,
        dateFilter,
        "total"
      )
    );
  } catch (err) {
    yield put(getAssessmentAnalyticsError(err));
  }
}

export function* getAssessmentAnalytics() {
  const watch = yield takeLatest(
    GET_ASSESSMENT_ANALYTICS,
    getAssessmentAnalyticsSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get previous assessment analytics -----------
export function* getPrevAssessmentAnalyticsSaga(action) {
  const getPrevAssessmentAnalyticsRequestURL = `${personalUrl}snapshotAnalytics/assessmentAnalyticsPrev`;
  try {
    const getPrevAssessmentAnalyticsResponse = yield call(
      request,
      getPrevAssessmentAnalyticsRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter: action.dateFilter }),
      }
    );
    if (getPrevAssessmentAnalyticsResponse.status === 204) {
      yield put(getPrevAssessmentAnalyticsSuccess(null));
    }
    yield put(
      getPrevAssessmentAnalyticsSuccess(
        getPrevAssessmentAnalyticsResponse.data,
        action.dateFilter,
        "prevTotal"
      )
    );
  } catch (err) {
    yield put(getPrevAssessmentAnalyticsError(err));
  }
}

export function* getPrevAssessmentAnalytics() {
  const watch = yield takeLatest(
    GET_PREV_ASSESSMENT_ANALYTICS,
    getPrevAssessmentAnalyticsSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get structured interview analytics -----------
export function* getStructuredInterviewAnalyticsSaga(action) {
  const getStructuredInterviewAnalyticsRequestURL = `${personalUrl}snapshotAnalytics/structuredInterviewAnalytics`;
  try {
    const { dateFilter, startDate, endDate } = action;

    const additionalData =
      dateFilter === "custom" ? { startDate, endDate } : {};

    const getStructuredInterviewAnalyticsResponse = yield call(
      request,
      getStructuredInterviewAnalyticsRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter, ...additionalData }),
      }
    );
    if (getStructuredInterviewAnalyticsResponse.status === 204) {
      yield put(getStructuredInterviewAnalyticsSuccess(null));
    }
    yield put(
      getStructuredInterviewAnalyticsSuccess(
        getStructuredInterviewAnalyticsResponse.data,
        action.dateFilter
      )
    );
  } catch (err) {
    yield put(getStructuredInterviewAnalyticsError(err));
  }
}

export function* getStructuredInterviewAnalytics() {
  const watch = yield takeLatest(
    GET_STRUCTURED_INTERVIEW_ANALYTICS,
    getStructuredInterviewAnalyticsSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get score range analytics -----------
export function* getScoreRangeAnalyticsSaga(action) {
  const getScoreRangeAnalyticsRequestURL = `${personalUrl}snapshotAnalytics/scoreRangeAnalytics`;
  try {
    const { dateFilter, startDate, endDate } = action;

    const additionalData =
      dateFilter === "custom" ? { startDate, endDate } : {};

    const getScoreRangeAnalyticsResponse = yield call(
      request,
      getScoreRangeAnalyticsRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter, ...additionalData }),
      }
    );
    if (getScoreRangeAnalyticsResponse.status === 204) {
      yield put(getScoreRangeAnalyticsSuccess(null));
    }
    yield put(
      getScoreRangeAnalyticsSuccess(
        getScoreRangeAnalyticsResponse.data,
        dateFilter
      )
    );
  } catch (err) {
    yield put(getScoreRangeAnalyticsError(err));
  }
}

export function* getScoreRangeAnalytics() {
  const watch = yield takeLatest(
    GET_SCORE_RANGE_ANALYTICS,
    getScoreRangeAnalyticsSaga
  );

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Get business value -----------
export function* getBusinessValueSaga(action) {
  const getBusinessValueRequestURL = `${personalUrl}snapshotAnalytics/businessValue`;
  try {
    const getBusinessValueResponse = yield call(
      request,
      getBusinessValueRequestURL,
      {
        method: "post",
        body: JSON.stringify({ dateFilter: action.dateFilter }),
      }
    );
    yield put(
      getBusinessValueSuccess(getBusinessValueResponse, action.dateFilter)
    );
  } catch (err) {
    yield put(getBusinessValueError(err));
  }
}

export function* getBusinessValue() {
  const watch = yield takeLatest(GET_BUSINESS_VALUE, getBusinessValueSaga);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

// --------- Track posthire login -----------
export function* trackPosthireLoginSaga() {
  const trackPosthireLoginRequestURL = `${personalUrl}talentInsights/trackDevelopLogon`;

  try {
    const trackPosthireLoginResponse = yield call(
      request,
      trackPosthireLoginRequestURL
    );
    trackPostHireV2Login();
    if (trackPosthireLoginResponse.status === 204) {
      yield put(trackPosthireLoginSuccess({}));
    }
    yield put(trackPosthireLoginSuccess(trackPosthireLoginResponse));
  } catch (err) {
    yield put(trackPosthireLoginError(err));
  }
}

export function* trackPosthireLogin() {
  const watch = yield takeLatest(TRACK_POSTHIRE_LOGIN, trackPosthireLoginSaga);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

/* ----------- Set Password----------- */

export function* setPassword() {
  const watch = yield takeLatest(SET_PASSWORD, setPasswordSaga);

  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* setPasswordSaga(action) {
  const requestURL = `${personalUrl}users/verify`;
  const { verifyToken, password } = action;

  try {
    const setPasswordResponse = yield call(request, requestURL, {
      method: "post",
      body: JSON.stringify({ verifyToken, password }),
    });

    yield put(setPasswordSuccess());

    if (setPasswordResponse.jwt && setPasswordResponse.data) {
      const userData = setPasswordResponse.data;
      const hasHireSelectAccess = userData.hireselectAccess;
      const hasPosthireAccess = checkIfUserHasPosthireAccess(userData);
      const hasDevelopByCriteriaAccess = userData.developByCriteriaAccess;
      const hsVersion = getVersionName(userData.hsVersion);

      const configCatObject = yield getConfigCatObject(userData);

      yield put(setupFeatureFlag(configCatObject));

      if (
        !hasDevelopByCriteriaAccess &&
        !hasHireSelectAccess &&
        !hasPosthireAccess
      ) {
        yield put(loginError(6)); // errorCode 6 means that the user does not have access to the prehire or posthire platform
        return;
      }

      if (hasDevelopByCriteriaAccess && !hasHireSelectAccess) {
        Cookies.set("critToken", setPasswordResponse.jwt, {
          path: "/",
          domain:
            new URL(developByCriteriaURL).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          setPasswordResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.location.href = developByCriteriaURL;
        return;
      }

      if (
        hasPosthireAccess &&
        !hasHireSelectAccess &&
        !hasDevelopByCriteriaAccess
      ) {
        Cookies.set("critToken", setPasswordResponse.jwt, {
          path: "/",
          domain:
            new URL(teamsUrl).hostname === "localhost"
              ? "localhost"
              : ".criteriacorp.com",
        });

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          setPasswordResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        );
        segmentIdentifyGroup(userData, hsVersion);
        window.analytics.track(
          "talent insights login",
          {
            "referral source type": "url",
            url: teamsUrl,
          },
          () => {
            window.location.href = teamsUrl;
          }
        );
      }

      if (hasHireSelectAccess) {
        const dateToday = new Date();
        const dateExpired = new Date(dateToday);

        dateExpired.setDate(dateExpired.getDate() + userData.daysRemaining);

        // we need the internal property of the token to determine if the user logged in from the intranet
        const jwtDecoded = jwtDecode(setPasswordResponse.jwt);
        const internal = !!jwtDecoded.internal;

        // do not send ChurnZero data if the user is internal
        if (!internal && userData.sfAccountId && userData.sfContactId) {
          setChurnZeroData(userData, hsVersion);
        }

        /* Segment SDK Event Tracking */
        // The Group tracking should come after User tracking.
        // This will make it so Segment attaches the User to the Account Group.
        segmentIdentifyUser(
          userData,
          setPasswordResponse.jwt,
          configCatObject.ftDashboardExperimentAllocation
        ).then(() => {
          segmentIdentifyGroup(userData, hsVersion).then(() => {
            window.analytics.track("User Login", {});
            if (userData && userData.isFreeTrial) {
              trackFTUserAssignedCTA(
                configCatObject.ftWelcomeBannerCtaButtonText || "Try Our Tests"
              );
            }
          });
        });

        // set JWT to localStorage for Criteria Platform scope
        yield localStorage.setItem("token", setPasswordResponse.jwt);

        // if logged in user has access to both Hireselect and Develop, we need to set the cross-subdomain subtoken here
        // so that if they click an external link to Develop (ex. email link) they will be directed to the Develop dashboard properly.
        if (hasPosthireAccess && configCatObject.isTeamsEnabled) {
          Cookies.set("critToken", setPasswordResponse.jwt, {
            path: "/",
            domain:
              new URL(developByCriteriaURL).hostname === "localhost"
                ? "localhost"
                : ".criteriacorp.com",
          });
        }

        yield put(getRoles());

        if (userData.daysRemaining > 0) {
          yield put(getCompanyInfo());
        } else {
          yield put(getCompanyInfo(true));
        }
      }

      yield put(
        loginSuccess(setPasswordResponse.jwt, userData, configCatObject)
      );

      if (userData.locale === "AU") {
        yield put(updateSourcesByLocale());
      }

      yield put(push("/"));
    }
  } catch (err) {
    yield put(setPasswordError(err));
  }
}

/* ----------- Resend Email Verification ----------- */

export function* resendVerificationEmail() {
  const watch = yield takeLatest(
    RESEND_VERIFICATION_EMAIL,
    resendVerificationEmailSaga
  );
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* resendVerificationEmailSaga(action) {
  const requestURL = `${personalUrl}users/resendVerify`;
  const { emailAddress } = action;

  try {
    const resendVerificationEmailResponse = yield call(request, requestURL, {
      method: "post",
      body: JSON.stringify({ emailAddress }),
    });

    yield put(resendVerificationEmailSuccess(resendVerificationEmailResponse));
  } catch (err) {
    yield put(resendVerificationEmailError(err));
  }
}

/* ----------- Check Valid Verify Token ----------- */

export function* checkValidVerifyToken() {
  const watch = yield takeLatest(
    CHECK_VALID_VERIFY_TOKEN,
    checkValidVerifyTokenSaga
  );
  // cancel the watch when location changes
  yield take(LOCATION_CHANGE);
  yield cancel(watch);
}

export function* checkValidVerifyTokenSaga(action) {
  const requestURL = `${personalUrl}users/checkVerifyToken`;
  const { verifyToken } = action;

  try {
    const decodedVerifyToken = jwtDecode(verifyToken);
    const emailAddress = decodedVerifyToken?.email;

    const checkValidVerifyTokenResponse = yield call(request, requestURL, {
      method: "post",
      body: JSON.stringify({ verifyToken, emailAddress }),
    });

    yield put(checkValidVerifyTokenSuccess(checkValidVerifyTokenResponse));
  } catch (err) {
    let errorData;

    yield err.response.json().then((response) => {
      errorData = response;
    });

    yield put(checkValidVerifyTokenError(errorData));
  }
}

// All sagas to be loaded
export default [
  trackPosthireLogin,
  ssoLogin,
  getIdentityProvider,
  filterOptions,
  login,
  refreshUserData,
  onAuthorizeLinkedInIntegration,
  partnersLogin,
  partnersForgotPassword,
  resetPassword,
  getInvoice,
  onGetCompanyInfo,
  roles,
  getBillingInfo,
  setNewPassword,
  setNewPasswordPartners,
  getInvoicePricing,
  users,
  getSources,
  createSource,
  updateFirstTeamsLogin,
  deleteCandidate,
  getDynamicScoreReportsData,
  getDynamicScoreReportsWorkStyles,
  getTestBatteries,
  sendSupportBotQuestion,
  sendResponseFeedback,
  getCandidateAnalytics,
  getPrevCandidateAnalytics,
  getAssessmentAnalytics,
  getPrevAssessmentAnalytics,
  getStructuredInterviewAnalytics,
  getScoreRangeAnalytics,
  getBusinessValue,
  setPassword,
  resendVerificationEmail,
  checkValidVerifyToken,
];
